import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  TextField,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export default function DateFilterDialog({ open, onClose, onApply }) {
  const [customFromMonth, setCustomFromMonth] = useState('');
  const [customToMonth, setCustomToMonth] = useState('');

  // Helper function to format date without timezone issues
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleQuickFilter = (type) => {
    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case 'today':
        startDate = endDate = formatDateLocal(today);
        break;
      case 'thisMonth':
        // First day of current month
        startDate = formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1));
        // Last day of current month
        endDate = formatDateLocal(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case 'thisYear':
        // January 1st of current year
        startDate = formatDateLocal(new Date(today.getFullYear(), 0, 1));
        // December 31st of current year
        endDate = formatDateLocal(new Date(today.getFullYear(), 11, 31));
        break;
      default:
        return;
    }

    onApply({ startDate, endDate });
    onClose();
  };

  const handleCustomApply = () => {
    if (!customFromMonth) {
      alert('Please select at least the From month');
      return;
    }

    const fromDate = new Date(customFromMonth);
    let toDate = fromDate; // Default to same month if no "To" month selected

    if (customToMonth) {
      toDate = new Date(customToMonth);
      if (fromDate > toDate) {
        alert('From month cannot be after To month');
        return;
      }
    }

    // Calculate start and end dates for the selected months
    const startDate = formatDateLocal(new Date(fromDate.getFullYear(), fromDate.getMonth(), 1));
    const endDate = formatDateLocal(new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0));

    onApply({ startDate, endDate });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: '#C4A574',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2}>
          {/* Custom Month Range */}
          <Box>
            <Box
              sx={{
                bgcolor: '#8B7355',
                color: 'white',
                p: 1,
                borderRadius: 1,
                mb: 2,
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              Custom Month Selection
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Box
                  sx={{
                    bgcolor: '#A0926B',
                    color: 'white',
                    p: 0.8,
                    borderRadius: 1,
                    mb: 1,
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  From Month *
                </Box>
                <TextField
                  type="month"
                  fullWidth
                  value={customFromMonth}
                  onChange={(e) => setCustomFromMonth(e.target.value)}
                  InputProps={{
                    sx: {
                      bgcolor: 'white',
                      borderRadius: 1,
                      color: '#bc965e',
                      '& input': {
                        color: '#bc965e',
                      },
                      '& input::placeholder': {
                        color: '#bc965e',
                        opacity: 0.7,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Box
                  sx={{
                    bgcolor: '#A0926B',
                    color: 'white',
                    p: 0.8,
                    borderRadius: 1,
                    mb: 1,
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  To Month (Optional)
                </Box>
                <TextField
                  type="month"
                  fullWidth
                  value={customToMonth}
                  onChange={(e) => setCustomToMonth(e.target.value)}
                  placeholder="Leave empty for single month"
                  InputProps={{
                    sx: {
                      bgcolor: 'white',
                      borderRadius: 1,
                      color: '#bc965e',
                      '& input': {
                        color: '#bc965e',
                      },
                      '& input::placeholder': {
                        color: '#bc965e',
                        opacity: 0.7,
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: '0.8rem',
                color: '#5D4E37',
                fontStyle: 'italic',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Select only "From Month" for a single month, or both for a range
            </Box>
          </Box>

          {/* Quick Filter Buttons */}
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleQuickFilter('today')}
            sx={{
              bgcolor: '#8B7355',
              color: 'white',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': { bgcolor: '#7A6449' },
            }}
          >
            Today
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => handleQuickFilter('thisMonth')}
            sx={{
              bgcolor: '#8B7355',
              color: 'white',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': { bgcolor: '#7A6449' },
            }}
          >
            This Month
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => handleQuickFilter('thisYear')}
            sx={{
              bgcolor: '#8B7355',
              color: 'white',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': { bgcolor: '#7A6449' },
            }}
          >
            This Year
          </Button>

          {/* Apply Custom Month Button */}
          {customFromMonth && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleCustomApply}
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': { bgcolor: '#45a049' },
              }}
            >
              {customToMonth ? 'Apply Month Range' : 'Apply Single Month'}
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
